import React, { useState, useMemo } from "react";
import {
  useGetCommunityEventsQuery,
  useDeleteCommunityEventMutation,
} from "../../../services/api";
import { getResourceIcon } from "../../../Components/icons/CommunityIcons";
import {
  getEventStatus,
  formatEventDate,
  getEventZoneLabel,
  getEventInstant,
} from "../../../utils/eventUtils";
import CommunityEventForm from "./CommunityEventForm";
import RSVPModal from "./RSVPModal";
import "./AdminCommunities.css";

/* tiny inline icons (no extra deps) */
const IconPlus = (props) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
    <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconDots = (props) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" {...props}>
    <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
  </svg>
);

/* Format date for display (timezone-safe: event dates are stored at UTC midnight,
   format in UTC so the calendar day never shifts for the viewer) */
function formatDate(dateString) {
  if (!dateString) return "-";
  return (
    formatEventDate(dateString, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }) || "-"
  );
}

/* Get end of current calendar week (Sunday 11:59:59 PM) */
function getEndOfWeek(now) {
  const endOfWeek = new Date(now);
  const dayOfWeek = endOfWeek.getDay(); // 0 = Sunday
  const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
  endOfWeek.setDate(endOfWeek.getDate() + daysUntilSunday);
  endOfWeek.setHours(23, 59, 59, 999);
  return endOfWeek;
}

/* Check if event falls within "this week" (uses the event's Eastern instant so
   today's events are grouped correctly regardless of the viewer's timezone) */
function isThisWeek(eventDate, endOfWeek) {
  if (!eventDate) return false;
  const date = getEventInstant(eventDate, "12:00");
  if (!date) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return date >= now && date <= endOfWeek;
}

/* Get the effective date for an event (next instance for recurring, nextEventDate for standalone) */
function getEffectiveDate(event) {
  return event.nextInstance?.date || event.nextEventDate;
}

/* Group and sort events into "This Week" and "Later" sections */
function groupEventsByTimeframe(events) {
  const now = new Date();
  const endOfWeek = getEndOfWeek(now);

  const thisWeek = [];
  const later = [];

  events.forEach((event) => {
    const date = getEffectiveDate(event);
    if (isThisWeek(date, endOfWeek)) {
      thisWeek.push(event);
    } else {
      later.push(event);
    }
  });

  // Sort by effective date ascending (Eastern instant)
  const sortByDate = (a, b) => {
    const dateA = getEventInstant(getEffectiveDate(a), a.nextInstance?.startTime || a.startTime);
    const dateB = getEventInstant(getEffectiveDate(b), b.nextInstance?.startTime || b.startTime);
    return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
  };
  thisWeek.sort(sortByDate);
  later.sort(sortByDate);

  return { thisWeek, later };
}

export default function AdminCommunities() {
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [rsvpModalTab, setRsvpModalTab] = useState("accepted");
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const { data, isLoading, error } = useGetCommunityEventsQuery({ isActive: true });
  const [deleteEvent] = useDeleteCommunityEventMutation();

  const events = data?.events || [];

  // Group events into "This Week" and "Later"
  const { thisWeek, later } = useMemo(
    () => groupEventsByTimeframe(events),
    [events]
  );

  const handleAddNew = () => {
    setEditingEvent(null);
    setShowForm(true);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setShowForm(true);
    setOpenMenuId(null);
  };

  const handleDelete = async (eventId) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      try {
        await deleteEvent(eventId).unwrap();
      } catch (err) {
        console.error("Failed to delete event:", err);
      }
    }
    setOpenMenuId(null);
  };

  const handleViewRSVPs = (eventId) => {
    setSelectedEventId(eventId);
    setRsvpModalTab("accepted");
    setShowRSVPModal(true);
    setOpenMenuId(null);
  };

  const handleViewPendingRSVPs = (eventId) => {
    setSelectedEventId(eventId);
    setRsvpModalTab("pending");
    setShowRSVPModal(true);
    setOpenMenuId(null);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEvent(null);
  };

  const toggleMenu = (eventId) => {
    setOpenMenuId(openMenuId === eventId ? null : eventId);
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openMenuId]);

  // Render a single event row
  const renderEventRow = (ev) => {
    // For recurring (parent) events, use the next upcoming instance date/time if available
    const effectiveDate = ev.nextInstance?.date || ev.nextEventDate;
    const effectiveStart = ev.nextInstance?.startTime || ev.startTime;
    const effectiveEnd = ev.nextInstance?.endTime || ev.endTime;
    const status = getEventStatus(effectiveDate, effectiveStart, effectiveEnd);

    return (
    <div key={ev._id || ev.id} className={`cop-row cop-row-${ev.category || "mint"}`}>
      <div className="td title">
        <div className="title-text">{ev.title}</div>
      </div>

      <div className="td status">
        <span className={`event-status-badge status-${status.key}`}>
          {status.key === "live" && <span className="status-dot" />}
          {status.label}
        </span>
      </div>

      <div className="td resources">
        {ev.resources && ev.resources.length > 0 ? (
          <div className="resources-cell">
            <a
              href={ev.resources[0].url}
              className="resource-link"
              target="_blank"
              rel="noopener noreferrer"
              title={ev.resources[0].title}
            >
              {getResourceIcon(ev.resources[0].type)}
              <span className="resource-link-title">{ev.resources[0].title}</span>
            </a>
            {ev.resources.length > 1 && (
              <span className="resource-more">+{ev.resources.length - 1}</span>
            )}
          </div>
        ) : (
          <span style={{ color: "#9ca3af" }}>-</span>
        )}
      </div>

      <div className="td meeting-link">
        {ev.meetingLink ? (
          <a
            href={ev.meetingLink}
            className="meeting-link-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            Link
          </a>
        ) : (
          <span style={{ color: "#9ca3af" }}>-</span>
        )}
      </div>

      <div className="td date">{formatDate(effectiveDate)}</div>
      <div className="td time">
        {effectiveStart} - {effectiveEnd} {getEventZoneLabel(effectiveDate, effectiveStart)}
      </div>

      <div className="td pending">
        {(ev.pendingCount || 0) > 0 ? (
          <button
            className="pending-badge"
            onClick={() => handleViewPendingRSVPs(ev._id || ev.id)}
          >
            {ev.pendingCount} pending
          </button>
        ) : (
          <span style={{ color: "#9ca3af" }}>-</span>
        )}
      </div>

      <div className="td menu" style={{ position: "relative" }}>
        <button
          className="cop-dots"
          aria-label="More actions"
          onClick={(e) => {
            e.stopPropagation();
            toggleMenu(ev._id || ev.id);
          }}
        >
          <IconDots />
        </button>
        {openMenuId === (ev._id || ev.id) && (
          <div className="cop-dropdown-menu">
            <button onClick={() => handleEdit(ev)}>Edit</button>
            <button onClick={() => handleViewRSVPs(ev._id || ev.id)}>
              View RSVPs
            </button>
            <button
              onClick={() => handleDelete(ev._id || ev.id)}
              className="danger"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
  };

  if (isLoading) {
    return (
      <div className="cop-wrap">
        <div className="cop-header">
          <h1 className="username">Communities Of Practice</h1>
        </div>
        <div className="cop-card">
          <p>Loading events...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cop-wrap">
        <div className="cop-header">
          <h1 className="username">Communities Of Practice</h1>
        </div>
        <div className="cop-card">
          <p>Error loading events. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cop-wrap">
      <button className="welcome cop-back" onClick={() => window.history.back()}>
        ← Back Home
      </button>

      <div className="cop-header">
        <h1 className="username">Communities Of Practice</h1>
        <button className="cop-add" onClick={handleAddNew}>
          <IconPlus /> <span>Add New Meeting</span>
        </button>
      </div>

      <div className="cop-card">
        <div className="cop-card-head">All Events</div>

        {events.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "#6b7280" }}>
            No events yet. Click "Add New Meeting" to create one.
          </div>
        ) : (
          <div className="cop-table">
            <div className="cop-thead">
              <div className="th title">Title</div>
              <div className="th status">Status</div>
              <div className="th resources">Resources</div>
              <div className="th meeting-link">Meeting Link</div>
              <div className="th date">Date</div>
              <div className="th time">Time</div>
              <div className="th pending">Pending</div>
              <div className="th menu" />
            </div>

            {/* This Week Section */}
            {thisWeek.length > 0 && (
              <div className="cop-section">
                <div className="cop-section-header">This Week</div>
                <div className="cop-tbody">
                  {thisWeek.map(renderEventRow)}
                </div>
              </div>
            )}

            {/* Later Section */}
            {later.length > 0 && (
              <div className="cop-section">
                <div className="cop-section-header">Later</div>
                <div className="cop-tbody">
                  {later.map(renderEventRow)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <CommunityEventForm event={editingEvent} onClose={handleCloseForm} />
      )}

      {/* RSVPs Modal (unified for accepted & pending) */}
      {showRSVPModal && (
        <RSVPModal
          eventId={selectedEventId}
          initialTab={rsvpModalTab}
          onClose={() => {
            setShowRSVPModal(false);
            setSelectedEventId(null);
          }}
        />
      )}
    </div>
  );
}
